// StickyScopeBar
//
// Renders a thin pinned chip at the top of the analysis pages showing
// what the user is currently analyzing (customer / filter / facility),
// with an X to clear it. Hidden when no scope is set, or when the user
// is on a page where the chip would be confusing (My Customers, My
// Filters, All Facilities).
//
// The chip is purely informational — clearing it via the X drops the
// scope from sessionStorage AND from every analysis page's local state
// (each page subscribes to ScopeContext and re-syncs when scope goes
// null).

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useScope } from '../contexts/ScopeContext';
import './StickyScopeBar.css';

// Pages where the StickyScopeBar is the only place the user sees their
// active scope. Pages with their own rich in-page scope display (a green
// "Viewing: <customer> [View facilities] [Clear]" bar) are deliberately
// excluded — having both produces 2x clear-buttons + 2x scope chips that
// confuse more than they help.
// Every analysis page now renders its own green "Viewing" bar, so the
// thin chip at the top is redundant on all of them. Keeping the empty
// allow-list (rather than deleting the component outright) lets us
// re-enable it on a per-page basis if some future page is added that
// doesn't yet have its own scope display.
const ANALYSIS_ROUTES = new Set([
  // '/customer-data' — uses its own green "Viewing" bar.
  // '/collaboration-analytics' — uses its own green "Viewing" bar.
  // '/insights' — uses its own green "Viewing" bar.
  // '/study-analytics' — uses its own green "Viewing" bar.
  // '/analytics' — uses its own green "Viewing" bar.
  // '/quick-refer' — uses its own green "Viewing" bar.
]);

const KIND_LABELS = {
  customer: 'Customer',
  filter:   'Saved Filter',
  facility: 'Facility',
};

const KIND_ICONS = {
  customer: '🏢',
  filter:   '🔖',
  facility: '🏥',
};

function StickyScopeBar() {
  const { scope, clearScope } = useScope();
  const location = useLocation();

  if (!scope) return null;
  if (!ANALYSIS_ROUTES.has(location.pathname)) return null;

  const kindLabel = KIND_LABELS[scope.kind] || 'Scope';
  const icon = KIND_ICONS[scope.kind] || '📌';

  return (
    <div className="sticky-scope-bar" role="status" aria-live="polite">
      <span className="sticky-scope-icon" aria-hidden="true">{icon}</span>
      <span className="sticky-scope-kind">{kindLabel}:</span>
      <span className="sticky-scope-label" title={scope.label}>
        {scope.label || `#${scope.id}`}
      </span>
      <button
        type="button"
        className="sticky-scope-clear"
        onClick={clearScope}
        aria-label={`Clear ${kindLabel.toLowerCase()} scope`}
        title="Clear this scope (also clears the selection on the current page)"
      >
        ×
      </button>
    </div>
  );
}

export default StickyScopeBar;
